<template>
  <ValidationProvider v-slot="{ errors }" name="TPA" rules="required">
    <AdvancedDropdown
      v-model="modelValue"
      v-bind="$attrs"
      :options="options"
      :disabled="isCompanyRoleName('TPA') || disabled"
      :can-add-new="false"
      label="TPA"
      size="sm"
    >
      <template v-slot:label> TPA <span class="text-danger">*</span> </template>

      <template v-slot:button-content>
        <template v-if="!!selectedOptions.length">
          {{ selectedOptions.join(', ') }}
        </template>
        <template v-else>Select TPA</template>
      </template>

      <template v-slot:item-extra="item">
        <a
          v-if="item.isNew"
          :class="{
            'text-light': modelValue.includes(item.value),
            'text-danger': !modelValue.includes(item.value)
          }"
          role="button"
          @click.prevent.stop="() => handleRemoveNewTPA(item)"
        >
          <BaseIcon icon="x" tag="i" />
        </a>
      </template>

      <template v-if="!isSuperUser" v-slot:add-new="{ cancel }">
        <BDXNewTPAForm
          size="sm"
          @cancel="cancel"
          @success="(newTPA) => handleNewTPAFormSuccess(newTPA, cancel)"
        />
      </template>
    </AdvancedDropdown>

    <div v-if="!!errors.length" class="invalid-feedback d-block">
      {{ errors[0] }}
    </div>
  </ValidationProvider>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api'
import { ValidationProvider } from 'vee-validate'

import {
  useCurrentUser,
  useUserCompany,
  useRLSGroup
} from '@composables'

import { BaseIcon } from '@components/base'
import { AdvancedDropdown } from '@components/advanced'
import { BDXNewTPAForm } from '@components/bdx'
import { KEEP_AS_IS_OPTION } from '@constants'

export default defineComponent({
  name: 'BDXEditFormTPA',
  components: {
    ValidationProvider,

    BaseIcon,

    AdvancedDropdown,

    BDXNewTPAForm
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    useKeepAsIsOption: Boolean,
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'created:tpa', 'removed:tpa'],
  setup(props, { emit }) {
    const { isSuperUser } = useCurrentUser()
    const { isRoleName: isCompanyRoleName } = useUserCompany()
    const { isFetching, rlsGroupItems, addRLSGroupItem, removeRLSGroupItem } =
      useRLSGroup('TPA')

    const modelValue = computed({
      get: () => props.value,
      set: (newValue) => emit('input', newValue)
    })

    const options = computed(() => {
      let options = rlsGroupItems.value
        .filter((rlsGroupItem) => {
          if (isSuperUser.value || rlsGroupItem.isNew) {
            return true
          }

          // Companies of the same type as the signed-in user are excluded;
          // every other type is selectable without requiring an RLS relationship.
          return !isCompanyRoleName('TPA')
        })
        .map((rlsGroupItem) => ({
          text: rlsGroupItem.TPA,
          value: rlsGroupItem.id,
          ...(rlsGroupItem.isNew && { disabled: true, isNew: true })
        }))
        if (props.useKeepAsIsOption) {
          options = [KEEP_AS_IS_OPTION, ...options];
        }
        return options;
      }
    )

    const selectedOptions = computed(() =>
      options.value
        .filter((option) => modelValue.value.includes(option.value))
        .map((option) => option.text)
    )

    // New TPA form
    const handleRemoveNewTPA = (TPA) => {
      removeRLSGroupItem(TPA.value)

      emit('removed:tpa', TPA)
    }

    const handleNewTPAFormSuccess = (newTPA, successCallback) => {
      if (isSuperUser.value) return

      const newItem = rlsGroupItems.value.find(
        (rlsGroupItem) => rlsGroupItem.isNew
      )

      if (!!newItem) {
        removeRLSGroupItem(newItem.id)

        emit('removed:tpa', newItem)
      }

      addRLSGroupItem(newTPA)

      emit('created:tpa', newTPA)

      successCallback()
    }

    return {
      isSuperUser,
      isCompanyRoleName,

      modelValue,

      isFetching,
      options,
      selectedOptions,

      handleRemoveNewTPA,
      handleNewTPAFormSuccess
    }
  }
})
</script>

<style scoped>
.advanced-dropdown >>> .dropdown-toggle {
  max-width: 214px;
}

.advanced-dropdown >>> .dropdown-menu .list-group-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.advanced-dropdown >>> .dropdown-menu .list-group-item.disabled {
  color: #fff;
  background-color: #1c4a50;
  pointer-events: initial;
}
</style>
